import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, Clock, Send, MessageCircle, Calendar, ArrowRight, Crown, CheckCircle, Award, Star } from 'lucide-react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    nume: '',
    telefon: '',
    email: '',
    data: '',
    mesaj: ''
  });
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.nume || !formData.telefon || !formData.email) {
      alert('Vă rugăm să completați toate câmpurile obligatorii (nume, telefon, email).');
      return;
    }

    setIsSubmitting(true);

    const mesajComplet = `
👑 SOLICITARE OFERTĂ BOTEZ DE POVESTE 👑

👤 Nume: ${formData.nume}
📱 Telefon: ${formData.telefon}
📧 Email: ${formData.email}
📅 Data evenimentului: ${formData.data || 'Nu a fost specificată'}

💌 Mesaj:
${formData.mesaj || 'Nu a fost introdus mesaj suplimentar'}

---
✨ Trimis de pe website-ul oficial BOTEZ DE POVESTE

📞 +40-740-048 888
    `.trim();

    const mailtoLink = `mailto:contact@trusoubotez.com?subject=👑 Solicitare Ofertă Botez - ${formData.nume}&body=${encodeURIComponent(mesajComplet)}`;
    
    setTimeout(() => {
      window.location.href = mailtoLink;
      setIsSubmitting(false);
      
      setFormData({
        nume: '',
        telefon: '',
        email: '',
        data: '',
        mesaj: ''
      });

      alert('✨ Mulțumim! Se va deschide aplicația de email cu mesajul pre-completat. Vă rugăm să-l trimiteți sau contactați-ne direct la +40-740-048 888.');
    }, 1000);
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Telefon Direct",
      content: "+40-740-048 888",
      link: "tel:+40740048888",
      description: "Apelați pentru consultanță gratuită",
      gradient: "from-emerald-500 to-emerald-700",
      shadowColor: "shadow-emerald"
    },
    {
      icon: Mail,
      title: "Email Oficial",
      content: "contact@trusoubotez.com",
      link: "mailto:contact@trusoubotez.com",
      description: "Răspundem în maxim 24 ore",
      gradient: "from-navy-500 to-navy-700",
      shadowColor: "shadow-navy"
    },
  
  ];

  return (
    <section id="contact" ref={sectionRef} className="py-20 bg-gradient-to-b from-white via-cream-50/30 to-white relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 -left-20 w-96 h-96 rounded-full bg-gradient-to-r from-navy-100/20 to-gold-100/20 blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 -right-20 w-128 h-128 rounded-full bg-gradient-to-r from-emerald-100/20 to-navy-100/20 blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-20 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center px-8 py-3 rounded-full glass border border-navy-200/50 text-navy-600 font-medium text-sm mb-6">
            <MessageCircle className="w-4 h-4 mr-2 text-gold-600 animate-bounce" />
            Să Vorbim despre Botezul Perfect
          </div>
          
          <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 font-display">
            <span className="gradient-text">Contactează-ne</span>
          </h3>
          
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Suntem aici pentru a vă ajuta să organizați botezul perfect pentru 
            <span className="gradient-text font-semibold"> copilul dumneavoastră</span>
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div className={`space-y-8 transition-all duration-1000 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            
            {/* Contact Cards */}
            {contactInfo.map((info, index) => (
              <div 
                key={index} 
                className={`group transition-all duration-500 ${inView ? 'fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className={`modern-card p-8 rounded-2xl hover-lift group-hover:${info.shadowColor} group-hover:shadow-xl transition-all duration-300 relative overflow-hidden`}>
                  
                  {/* Background Gradient on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${info.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl`}></div>
                  
                  <div className="relative flex items-start">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${info.gradient} flex items-center justify-center mr-6 flex-shrink-0 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                      <info.icon className="w-8 h-8 text-white" />
                    </div>
                    
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-navy-600 transition-colors duration-300">
                        {info.title}
                      </h4>
                      
                      {info.link ? (
                        <a 
                          href={info.link} 
                          className="text-lg font-semibold text-navy-600 hover:text-navy-700 transition-colors duration-300 block mb-2"
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-lg font-semibold text-gray-800 mb-2">
                          {info.content}
                        </p>
                      )}
                      
                      <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                        {info.description}
                      </p>
                    </div>
                    
                    {/* Floating particle on hover */}
                    <div className="absolute -top-2 -right-2 w-3 h-3 bg-gold-400 rounded-full opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300"></div>
                  </div>
                </div>
              </div>
            ))}

            
          </div>

          {/* Contact Form */}
          <div className={`transition-all duration-1000 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`} style={{ transitionDelay: '0.3s' }}>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cream-100/50 to-gold-100/30 rounded-3xl blur-xl"></div>
              <div className="relative modern-card rounded-3xl p-8 shadow-2xl">
                
                <div className="text-center mb-8">
                  <h4 className="text-3xl font-bold text-gray-900 mb-3 font-display">
                    Solicită o <span className="gradient-text">Ofertă Personalizată</span>
                  </h4>
                  <p className="text-gray-600">Completați formularul și vă vom contacta în maxim 24 de ore</p>
                </div>
                
                <div className="space-y-6">
                  {/* Nume si Telefon */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="relative group">
                      <label className="block text-gray-700 font-semibold mb-2 group-hover:text-navy-600 transition-colors duration-300">
                        Nume Complet *
                      </label>
                      <input 
                        type="text" 
                        name="nume"
                        value={formData.nume}
                        onChange={handleInputChange}
                        onFocus={() => setFocusedField('nume')}
                        onBlur={() => setFocusedField(null)}
                        className={`w-full px-4 py-4 border-2 rounded-xl transition-all duration-300 ${
                          focusedField === 'nume' 
                            ? 'border-navy-500 shadow-navy shadow-lg scale-105' 
                            : 'border-gray-200 hover:border-navy-300'
                        }`}
                        placeholder="Introduceți numele complet"
                      />
                    </div>
                    <div className="relative group">
                      <label className="block text-gray-700 font-semibold mb-2 group-hover:text-navy-600 transition-colors duration-300">
                        Telefon *
                      </label>
                      <input 
                        type="tel" 
                        name="telefon"
                        value={formData.telefon}
                        onChange={handleInputChange}
                        onFocus={() => setFocusedField('telefon')}
                        onBlur={() => setFocusedField(null)}
                        className={`w-full px-4 py-4 border-2 rounded-xl transition-all duration-300 ${
                          focusedField === 'telefon' 
                            ? 'border-navy-500 shadow-navy shadow-lg scale-105' 
                            : 'border-gray-200 hover:border-navy-300'
                        }`}
                        placeholder="+40 xxx xxx xxx"
                      />
                    </div>
                  </div>
                  
                  {/* Email */}
                  <div className="relative group">
                    <label className="block text-gray-700 font-semibold mb-2 group-hover:text-navy-600 transition-colors duration-300">
                      Email *
                    </label>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      className={`w-full px-4 py-4 border-2 rounded-xl transition-all duration-300 ${
                        focusedField === 'email' 
                          ? 'border-navy-500 shadow-navy shadow-lg scale-105' 
                          : 'border-gray-200 hover:border-navy-300'
                      }`}
                      placeholder="email@exemplu.ro"
                    />
                  </div>

                  {/* Data */}
                  <div className="relative group">
                    <label className="block text-gray-700 font-semibold mb-2 group-hover:text-navy-600 transition-colors duration-300">
                      <Calendar className="w-4 h-4 inline mr-2" />
                      Data Planificată a Botezului
                    </label>
                    <input 
                      type="date" 
                      name="data"
                      value={formData.data}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('data')}
                      onBlur={() => setFocusedField(null)}
                      className={`w-full px-4 py-4 border-2 rounded-xl transition-all duration-300 ${
                        focusedField === 'data' 
                          ? 'border-navy-500 shadow-navy shadow-lg scale-105' 
                          : 'border-gray-200 hover:border-navy-300'
                      }`}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  {/* Mesaj */}
                  <div className="relative group">
                    <label className="block text-gray-700 font-semibold mb-2 group-hover:text-navy-600 transition-colors duration-300">
                      Mesajul Dumneavoastră
                    </label>
                    <textarea 
                      rows="5" 
                      name="mesaj"
                      value={formData.mesaj}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('mesaj')}
                      onBlur={() => setFocusedField(null)}
                      className={`w-full px-4 py-4 border-2 rounded-xl resize-none transition-all duration-300 ${
                        focusedField === 'mesaj' 
                          ? 'border-navy-500 shadow-navy shadow-lg scale-105' 
                          : 'border-gray-200 hover:border-navy-300'
                      }`}
                      placeholder="Descrieți-ne viziunea dumneavoastră pentru botez, numărul aproximativ de invitați, locația preferată sau orice alte detalii importante..."
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <button 
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 relative overflow-hidden group ${
                      isSubmitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 hover:scale-105 shadow-navy hover:shadow-2xl'
                    } text-white`}
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      {isSubmitting ? (
                        <>
                          <div className="loading-dots mr-2">
                            <span className="w-2 h-2 bg-white rounded-full inline-block mr-1"></span>
                            <span className="w-2 h-2 bg-white rounded-full inline-block mr-1"></span>
                            <span className="w-2 h-2 bg-white rounded-full inline-block"></span>
                          </div>
                          Se trimite...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform duration-300" />
                          Trimite Solicitarea
                          <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                        </>
                      )}
                    </span>
                  </button>
                  
                  <p className="text-sm text-gray-600 text-center flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
                    Câmpurile marcate cu * sunt obligatorii. Răspundem în maxim 24 de ore.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className={`mt-20 text-center transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: '1s' }}>
          <div className="relative max-w-5xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-r from-navy-500/10 via-gold-500/10 to-emerald-500/10 rounded-3xl blur-xl"></div>
            <div className="relative modern-card rounded-3xl p-12 shadow-2xl hover-lift">
              
              <div className="mb-8">
                <div className="flex justify-center mb-6">
                  <div className="flex -space-x-4">
                    {[Crown, Award, Star].map((Icon, index) => (
                      <div 
                        key={index} 
                        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg float border-4 border-white ${
                          index === 0 ? 'bg-gradient-to-br from-navy-600 to-navy-700' :
                          index === 1 ? 'bg-gradient-to-br from-gold-600 to-gold-700' :
                          'bg-gradient-to-br from-emerald-600 to-emerald-700'
                        }`}
                        style={{ animationDelay: `${index * 0.3}s` }}
                      >
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                    ))}
                  </div>
                </div>
                
                <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-display">
                  Gata să Începem <span className="gradient-text">Planificarea</span>?
                </h4>
                
                <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                  Contactați-ne astăzi pentru o <span className="font-semibold text-navy-600">consultație gratuită</span> 
                  și să discutăm despre botezul de poveste al copilului dumneavoastră!
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:+40740048888"
                  className="morph-button px-10 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-full hover:from-emerald-700 hover:to-emerald-800 font-bold text-lg shadow-emerald hover:shadow-2xl transition-all duration-300"
                >
                  <span className="flex items-center justify-center">
                    <Phone className="w-5 h-5 mr-2" />
                    Sună Acum
                  </span>
                </a>
                
                <a 
                  href="mailto:contact@trusoubotez.com"
                  className="morph-button px-10 py-4 border-2 border-navy-600 text-navy-600 rounded-full hover:bg-navy-600 hover:text-white font-bold text-lg transition-all duration-300"
                >
                  <span className="flex items-center justify-center">
                    <Mail className="w-5 h-5 mr-2" />
                    Trimite Email
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-32 left-8 w-4 h-4 bg-navy-300 rounded-full opacity-40 particle-1"></div>
      <div className="absolute top-64 right-16 w-3 h-3 bg-gold-400 rounded-full opacity-30 particle-2"></div>
      <div className="absolute bottom-32 left-20 w-2 h-2 bg-emerald-300 rounded-full opacity-50 particle-1"></div>
      <div className="absolute bottom-64 right-12 w-5 h-5 bg-navy-300 rounded-full opacity-20 particle-2"></div>
    </section>
  );
};

export default ContactSection;